import { useEffect } from 'react'
import { Modal } from 'almanac-ui/Modal'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Text } from 'blaze-ui/Text/Text'
import { Button } from 'blaze-ui/Button'
import { ArrowRight, Upload5, Close } from 'blaze-ui/icons'
import { ImageThumbnail } from 'components/ImagePickerModal/ImageThumbnail/ImageThumbnail'
import { FileTrigger } from 'components/FileTrigger/FileTrigger'
import { FooterButtons, StickyFooter } from 'components/FirstTimeExperienceModal/components/Footer/Footer'
import { StickyFooterProvider } from 'providers/StickyFooterProvider/StickyFooterProvider'
import { ACCEPTED_IMAGE_AND_VIDEO_MIME_TYPES } from 'screens/Finder/components/views/GalleryView/constants'
import { trackEventInHeap, HeapEventType } from 'utils/heapTracking'
import { useMediaSelection, mediaFileToSelectedImage } from '../hooks'
import Styles from '../VisualStylesForExistingUsersModal.module.scss'

interface MediaSelectionStepProps {
  selectedMediaFileId: number | null
  onSelectMediaFile: (id: number) => void
  onNext: () => void
  onBack: () => void
  onClose: () => void
  brandKitId: number
}

export const MediaSelectionStep = ({
  selectedMediaFileId,
  onSelectMediaFile,
  onNext,
  onBack,
  onClose,
  brandKitId,
}: MediaSelectionStepProps) => {
  const { mediaFiles, isLoading, isUploading, uploadProgress, handleSelectMediaFile, handleUpload } = useMediaSelection(
    {
      brandKitId,
      selectedMediaFileId,
      onSelectMediaFile,
    },
  )

  useEffect(() => {
    trackEventInHeap(HeapEventType.VisualStylesForExistingUsers, {
      action: 'view-media-selection-step',
    })
  }, [])

  const handleUploadAndProceed = async (files: FileList | null) => {
    const uploadedFiles = await handleUpload(files)
    // If files were uploaded successfully, select the first one and proceed to next step
    if (uploadedFiles && uploadedFiles.length > 0 && uploadedFiles[0].media_file_id) {
      const mediaFileId = uploadedFiles[0].media_file_id
      onSelectMediaFile(mediaFileId)
      trackEventInHeap(HeapEventType.VisualStylesForExistingUsers, {
        action: 'complete-media-selection-step',
        selected_media_file_id: mediaFileId,
      })
      onNext()
    }
  }

  const handleNext = () => {
    trackEventInHeap(HeapEventType.VisualStylesForExistingUsers, {
      action: 'complete-media-selection-step',
      selected_media_file_id: selectedMediaFileId ?? undefined,
    })
    onNext()
  }

  const footer = (
    <StickyFooter>
      <FooterButtons>
        <div className={Styles.footerSide}>
          <Button variant="tertiary" size="lg" onPress={onBack} aria-label="Back">
            Back
          </Button>
        </div>
        <div className={Styles.footerSide}>
          <Button
            variant="primary"
            size="lg"
            endIcon={ArrowRight}
            onPress={handleNext}
            isDisabled={!selectedMediaFileId || isUploading}
            aria-label="Choose style selection"
          >
            Choose style selection
          </Button>
        </div>
      </FooterButtons>
    </StickyFooter>
  )

  return (
    <Modal.Content className={Styles.mediaSelectionContent}>
      <Button size="sm" variant="tertiary" onPress={onClose} className={Styles.stepCloseButton}>
        <Close />
      </Button>
      <StickyFooterProvider footer={footer}>
        <div className={Styles.stepContent}>
          <div className={Styles.stepHeader}>
            <Heading level={1}>Choose one of your Brand Kit images to try Stylized Images and AI Video.</Heading>
            <Text color="var(--dark-40)" variant="primary">
              Next, we'll create a professional visual style for all your content
            </Text>
          </div>

          <div className={Styles.mediaSelectionGrid}>
            {isLoading &&
              Array.from({ length: 9 }).map((_, i) => (
                <div key={`skeleton-${i}`} className={Styles.mediaSelectionItem}>
                  <ImageThumbnail image={{ url: '' }} forceLoading />
                </div>
              ))}
            {!isLoading &&
              mediaFiles.map((file) => (
                <div key={file.media_file_id} className={Styles.mediaSelectionItem}>
                  <ImageThumbnail
                    image={mediaFileToSelectedImage(file)}
                    isSelected={selectedMediaFileId === file.media_file_id}
                    onToggleSelect={handleSelectMediaFile}
                  />
                </div>
              ))}
            {!isLoading && (
              <FileTrigger
                isDisabled={isUploading}
                accept={ACCEPTED_IMAGE_AND_VIDEO_MIME_TYPES}
                allowsMultiple
                onSelect={handleUploadAndProceed}
              >
                <Button variant="tertiary" className={Styles.uploadNewButton} aria-label="Upload new media">
                  {isUploading ? (
                    <Text variant="secondary" color="var(--dark-90)">
                      {uploadProgress.toFixed(0)}%
                    </Text>
                  ) : (
                    <div className={Styles.uploadNewButtonContent}>
                      <Upload5 size={24} color="var(--dark-90)" />
                      <Text variant="secondary" color="var(--dark-90)">
                        Upload New
                      </Text>
                    </div>
                  )}
                </Button>
              </FileTrigger>
            )}
          </div>
        </div>
      </StickyFooterProvider>
    </Modal.Content>
  )
}
